import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Shield } from "lucide-react";

export default function POSH() {
  const [cases, setCases] = useState([]);
  const [report, setReport] = useState(null);
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/posh/cases"), api.get("/posh/report/annual")]);
      setCases(c.data); setReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Access denied");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.patch(`/posh/cases/${editing.case_id}`, { status: editing.status, outcome: editing.outcome, note: note || null });
    toast.success("Updated"); setEditing(null); setNote(""); load();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Shield size={20} className="text-[#5B9BFF]" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">POSH cases</h1>
          <p className="text-sm text-[#A1A1AA]">Sealed, Internal Committee only. All views are audited.</p>
        </div>
      </div>

      {report && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="tt-card p-4"><div className="tt-label mb-2">Received ({report.year})</div><div className="font-mono text-2xl">{report.received}</div></div>
          <div className="tt-card p-4"><div className="tt-label mb-2">Disposed</div><div className="font-mono text-2xl text-[#22C55E]">{report.disposed}</div></div>
          <div className="tt-card p-4"><div className="tt-label mb-2">Pending</div><div className="font-mono text-2xl text-[#F5A524]">{report.pending}</div></div>
        </div>
      )}

      <div className="tt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-xs font-mono"><th className="text-left px-4 py-2">Case</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Received</th><th className="text-left px-4 py-2">Anonymous</th></tr></thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.case_id} className="border-t border-[#1E1E1E] hover:bg-[#121212] cursor-pointer" onClick={() => setEditing({ ...c })}>
                <td className="px-4 py-3"><div className="font-medium">{c.summary.slice(0, 80)}</div><div className="text-xs font-mono text-[#71717A]">{c.case_id}</div></td>
                <td className="px-4 py-3"><span className="tt-badge-info">{c.status}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(c.received_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">{c.anonymous ? "Yes" : "No"}</td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-[#71717A]">No cases.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="w-full max-w-xl tt-card p-6 my-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h2 className="text-lg font-semibold">Case {editing.case_id}</h2><button onClick={() => setEditing(null)} className="tt-btn-ghost"><X size={16} /></button></div>
            <div className="text-sm text-[#A1A1AA] mb-4 whitespace-pre-wrap">{editing.summary}</div>
            <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="tt-input mb-3">
              {["received", "investigating", "action_taken", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input className="tt-input mb-3" placeholder="Outcome" value={editing.outcome || ""} onChange={(e) => setEditing({ ...editing, outcome: e.target.value })} />
            <textarea rows={3} className="tt-input mb-3" placeholder="Add timeline note" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="tt-label mb-1">Timeline</div>
            <div className="text-xs font-mono space-y-1 max-h-40 overflow-y-auto mb-4">
              {(editing.timeline || []).map((t, i) => <div key={i}>{t.ts?.slice(0,10)} · {t.by}: {t.note}</div>)}
            </div>
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={save} className="tt-btn-primary text-sm">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
